import { IntlShape } from 'react-intl';
import { messages } from '../../i18n';
import { LOCALIZED_PATHS, localizeRoute, delocalizeRoute } from '../constants/url-constants';
import { normalizeFilterKey, normalizeFilterValue } from '../facets/normalize-filter-value';
import { buildEncodedLocationSrpPathSegment } from '../location/build-encoded-location-srp-path-segment';
import { decodeLocationSlug } from '../location/decode-location-slug';
import {
  decodeOpaqueUrlPathSegment,
  encodeOpaqueUrlPathSegment,
} from '../routing/encode-opaque-url-path-segment';
import { isSummerJobsResourcePathname } from '../routing/summer-jobs-resource-paths';
import {
  inferAppLocaleFromPathname,
  summerJobTypeFacetForLocale,
} from '../job/summer-job-job-type';
import { parseAlgoliaNamespacedParamKey } from './parse-algolia-namespaced-param';
import { isSrpNonFilterQueryParam } from '../search/srp-non-filter-query-params';
import { getCategoryBySlug, getCategoryFieldValuesForLocale } from '../category/category-hub-config';
import { getRoleBySlug, isRolePathMarker, fieldValuesMatchRole } from '../role/role-hub-config';

/** Localized path segment after /jobs for company facet (mirrors location → alue → omrade). */
export const JOBS_COMPANY_PATH_SEGMENT: Record<'en' | 'fi' | 'sv', string> = {
  en: 'company',
  fi: 'yritys',
  sv: 'foretag',
};

export interface GenerateUrlFromIndexUiStateOptions {
  /**
   * When true, never emit `/{locale}/company/{id}` (employer profile).
   * Use for links that must stay on the jobs listing with a path-encoded company (e.g. “See all jobs”).
   */
  forceJobsListingForCompany?: boolean;
}

const JOBS_LISTING_SEGMENTS = new Set(['jobs', 'tyopaikat', 'lediga-jobb', 'jobb']);

/**
 * True when the pathname includes a localized jobs listing segment (e.g. `/en/jobs`, `/fi/tyopaikat/yritys/…`).
 * Used when syncing search URL state so we do not collapse sole `companyId` filters to employer profile URLs
 * while the user is on the SRP — that would replace `/en/jobs/company/x` with `/en/company/x`, which does not
 * match the index route and breaks filters and navigation.
 */
export function pathnameIsJobsListingPath(pathname: string): boolean {
  const raw = pathname.split('?')[0] || '';
  const segments = raw.split('/').filter(Boolean);
  return segments.some((s) => JOBS_LISTING_SEGMENTS.has(s.toLowerCase()));
}

/** True when pathname is an employer profile route (localized: `/fi/yritys/slug`, `/sv/arbetsgivare/slug`, etc.). */
export function isCompanyProfilePath(pathnameOrHref: string): boolean {
  const path = pathnameOrHref.split('?')[0] || '';
  return /^\/(?:en|fi|sv)\/(?:company|yritys|arbetsgivare)\/[^/]+$/i.test(path);
}

function countRefinementAttributes(ref: Record<string, string[] | undefined> | undefined, exclude: Set<string>): number {
  if (!ref) return 0;
  let n = 0;
  for (const [k, v] of Object.entries(ref)) {
    if (exclude.has(k)) continue;
    const arr = Array.isArray(v) ? v : [];
    if (arr.some((x) => x != null && String(x).trim() !== '')) n++;
  }
  return n;
}

export interface IndexUiState {
  [key: string]: any;
  query?: string;
  page?: number;
  sort?: 'relevance' | 'date' | 'salary';
  /** When false, show jobs globally; when true or omitted, show Finland-only. Stored as URL param `finland`. */
  isFinland?: boolean;
  /** SRP view mode override. Only 'map' is ever set explicitly; 'list' (the default) is represented by omission. Stored as URL param `view`. */
  view?: 'list' | 'map';
  refinementList?: {
    [attribute: string]: string[];
  };
  // Add other UI state properties as needed
}

/**
 * Parses a URL pathname into an IndexUiState object
 * @param pathname The URL pathname to parse
 * @returns An IndexUiState object containing the parsed parameters
 */
// SIMPLE VERSION FOR DEBUGGING
export function parseUrlToIndexUiState(pathname: string): IndexUiState {
  const isServer = typeof window === 'undefined';
  
  // Simple: just parse query parameters, ignore path structure
  let urlObj: URL | null = null;
  try {
    urlObj = new URL(pathname, 'http://localhost');
  } catch {}
  const searchParams = urlObj ? urlObj.searchParams : new URLSearchParams(pathname.split('?')[1] || '');
  const pathForLocaleEarly = urlObj ? urlObj.pathname : pathname.split('?')[0] || '/';
  const appLocale = inferAppLocaleFromPathname(pathForLocaleEarly);

  const state: IndexUiState = {};
  
  // Parse basic query parameters
  const refinementList: Record<string, string[]> = {};
  
  // ADDING BACK: Extract location from path - handle multiple URL patterns like route.ts
  try {
    const rawPath = urlObj ? urlObj.pathname : pathname.split('?')[0] || '/';
    const segs = rawPath.split('/').filter(Boolean);
    
    // Determine if first segment is a locale
    const firstSegment = segs[0]?.toLowerCase();
    const isFirstSegmentLocale = firstSegment && ['en', 'fi', 'sv'].includes(firstSegment);

    // Standalone employer profile: /{locale}/…/slug (logical path /company/{slug})
    if (isFirstSegmentLocale && segs.length === 3) {
      const withoutLocale = `/${segs[1]}/${segs[2]}`;
      const loc = firstSegment as keyof typeof LOCALIZED_PATHS;
      const deloc = delocalizeRoute(withoutLocale, loc);
      const m = deloc.match(/^\/company\/([^/]+)$/);
      if (m) {
        refinementList.companyId = [
          normalizeFilterValue('companyId', decodeOpaqueUrlPathSegment(m[1]), appLocale),
        ];
      }
    }

    // Find jobs segment index (accounting for optional locale prefix)
    const jobsIdx = segs.findIndex((s) => s === 'jobs' || s === 'tyopaikat' || s === 'lediga-jobb' || s === 'jobb');
    
    if (jobsIdx !== -1) {
      const seg1 = segs[jobsIdx + 1];

      if (
        seg1 &&
        (seg1 === 'location' ||
          seg1 === 'alue' ||
          seg1 === 'omrade' ||
          seg1 === 'område' ||
          seg1 === 'omr%C3%A5de')
      ) {
        const locSeg = segs[jobsIdx + 2];
        if (locSeg) {
          const tokens = locSeg.split('+');
          const decodedList = tokens.map((t) => decodeLocationSlug(t)).filter(Boolean);
          if (decodedList.length > 0) {
            refinementList.location = decodedList;
          }
        }
      }

      // Optional /jobs/.../company/{slug} (after optional /location/… segment)
      let cursor = jobsIdx + 1;
      const marker = segs[cursor];
      if (
        marker &&
        (marker === 'location' ||
          marker === 'alue' ||
          marker === 'omrade' ||
          marker === 'område' ||
          marker === 'omr%C3%A5de')
      ) {
        cursor += 2;
      }
      const companyMarker = segs[cursor];
      const companySlug = segs[cursor + 1];
      if (
        companyMarker &&
        companySlug &&
        ['company', 'yritys', 'foretag'].includes(companyMarker.toLowerCase())
      ) {
        refinementList.companyId = [
          normalizeFilterValue('companyId', decodeOpaqueUrlPathSegment(companySlug), appLocale),
        ];
        cursor += 2;
      }

      // Optional /jobs/.../category/{slug} (after optional /location/… and /company/… segments).
      // Category slugs are noisy `field` facet buckets — resolve through the curated config so
      // every spelling variant for the category is OR-filtered together.
      const categoryMarker = segs[cursor];
      const categorySlug = segs[cursor + 1];
      if (
        categoryMarker &&
        categorySlug &&
        ['category', 'kategoria', 'kategori'].includes(categoryMarker.toLowerCase())
      ) {
        const category = getCategoryBySlug(decodeURIComponent(categorySlug));
        if (category) {
          refinementList.field = getCategoryFieldValuesForLocale(category, appLocale);
        }
        cursor += 2;
      }

      // Optional /jobs/.../role/{slug} (fi: ammatti, sv: yrke). Role field values are
      // per-locale — each Typesense collection stores `field` in its own language.
      const roleMarker = segs[cursor];
      const roleSlug = segs[cursor + 1];
      if (roleMarker && roleSlug && isRolePathMarker(roleMarker)) {
        const roleDef = getRoleBySlug(decodeURIComponent(roleSlug));
        if (roleDef) {
          refinementList.field = roleDef.fieldValues[appLocale] ?? roleDef.fieldValues.en;
        }
      }
    }
  } catch (error) {
    // Silently handle path parsing errors
  }
  
  // List of tracking/marketing parameters to ignore (not treated as search filters)
  const ignoredParams = new Set([
    // UTM parameters (Google Ads, marketing campaigns)
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_term',
    'utm_content',
    // Google Click ID
    'gclid',
    // Google Browser ID (used by Google crawlers)
    'gbraid',
    'wbraid',
    // Facebook Click ID
    'fbclid',
    // Microsoft Click ID
    'msclkid',
    // Other common tracking parameters
    'ref',
    'source',
    'medium',
    'campaign',
    'term',
    'content',
    // Google Analytics
    '_ga',
    '_gid',
    // Other analytics/tracking
    'mc_cid',
    'mc_eid',
    // Auth / navigation (not part of job search state)
    'returnTo',
    'utm_id',
    'utm_source_platform',
    'utm_creative_format',
    'utm_marketing_tactic',
  ]);

  for (const [key, value] of searchParams.entries()) {
    // Normalize parameter key to lowercase for case-insensitive matching
    const normalizedKey = normalizeFilterKey(key);

    // Skip ignored tracking parameters and app-only query keys (e.g. search_provider)
    if (ignoredParams.has(normalizedKey) || isSrpNonFilterQueryParam(key)) {
      continue;
    }

    // Legacy InstantSearch namespaced keys (e.g. sv_sok[refinementList][remoteness][0]=...)
    // must map to real facet attributes; otherwise Algolia gets impossible filters and returns 0 hits.
    const namespaced = parseAlgoliaNamespacedParamKey(key);
    if (namespaced.kind === 'query') {
      state.query = value;
      continue;
    }
    if (namespaced.kind === 'page') {
      const pageNum = parseInt(value, 10);
      if (!isNaN(pageNum)) {
        state.page = pageNum;
      }
      continue;
    }
    if (namespaced.kind === 'sort') {
      continue;
    }
    if (namespaced.kind === 'refinement') {
      const filterKey = namespaced.attribute;
      if (!refinementList[filterKey]) {
        refinementList[filterKey] = [];
      }
      refinementList[filterKey].push(normalizeFilterValue(filterKey, value, appLocale));
      continue;
    }

    if (normalizedKey === 'query' || normalizedKey === 'search') {
      state.query = value;
    } else if (normalizedKey === 'page') {
      const pageNum = parseInt(value, 10);
      if (!isNaN(pageNum)) {
        state.page = pageNum;
      }
    } else if (normalizedKey === 'sort') {
      if (value === 'relevance' || value === 'date' || value === 'salary') {
        state.sort = value;
      }
    } else if (normalizedKey === 'finland' || normalizedKey === 'isfinland') {
      state.isFinland = value === '1' || value === 'true';
    } else if (normalizedKey === 'view') {
      // Only an explicit 'map' is treated as an override; any other/missing value means
      // "no override" rather than an explicit 'list', so cookie/localStorage prefs can still apply.
      if (value === 'map') {
        state.view = 'map';
      }
    } else if (key.includes('[sort]') || key.endsWith('[sort]')) {
      // Skip old format sort parameters like "en_search[sort]"
      // These are legacy parameters that should be ignored
      continue;
    } else {
      // Treat other parameters as filters — use flat key as Algolia attribute (e.g. jobType, remoteness)
      const filterKey = key;
      if (!refinementList[filterKey]) {
        refinementList[filterKey] = [];
      }
      refinementList[filterKey].push(normalizeFilterValue(filterKey, value, appLocale));
    }
  }

  // Legacy URLs used ?isSummerJob=true — map to locale-specific jobType facet
  const pathForLocale = pathForLocaleEarly;
  const summerFlags = refinementList.isSummerJob;
  if (summerFlags?.some((v) => v === 'true' || v === '1')) {
    delete refinementList.isSummerJob;
    const loc = inferAppLocaleFromPathname(pathForLocale);
    const facet = summerJobTypeFacetForLocale(loc);
    if (!refinementList.jobType) refinementList.jobType = [];
    if (!refinementList.jobType.includes(facet)) {
      refinementList.jobType.push(facet);
    }
  }
  
  // Add refinementList if we have any filters
  if (Object.keys(refinementList).length > 0) {
    state.refinementList = refinementList;
  }
  
  return state;
}

// SIMPLE VERSION FOR DEBUGGING
export function generateUrlFromIndexUiState(
  indexUiState: IndexUiState,
  indexName: string,
  originalPath?: string,
  options?: GenerateUrlFromIndexUiStateOptions,
): string {
  const params = new URLSearchParams();
  // Infer locale from index name like "en_search" | "fi_hae" | "sv_sok"
  const localeMatch = indexName.match(/^([a-z]{2})[_-]/i);
  const locale = ((localeMatch ? localeMatch[1] : 'en').toLowerCase() as 'en' | 'fi' | 'sv') || 'en';

  const refEarly = indexUiState.refinementList || {};
  const companyIdsEarly = refEarly.companyId || [];
  const soleCompanyId =
    companyIdsEarly.length === 1 && String(companyIdsEarly[0] ?? '').trim()
      ? String(companyIdsEarly[0]).trim()
      : '';
  const locEarly = refEarly.location || [];
  const hasQueryEarly = Boolean(indexUiState.query && indexUiState.query.trim().length > 0);
  const hasSortEarly = Boolean(indexUiState.sort && indexUiState.sort !== 'relevance');
  const hasPageEarly =
    indexUiState.page !== undefined && indexUiState.page !== null && indexUiState.page > 0;
  const otherRefinementCount = countRefinementAttributes(
    refEarly as Record<string, string[] | undefined>,
    new Set(['companyId', 'location']),
  );

  if (
    soleCompanyId &&
    !options?.forceJobsListingForCompany &&
    locEarly.length === 0 &&
    !hasQueryEarly &&
    !hasSortEarly &&
    !hasPageEarly &&
    otherRefinementCount === 0
  ) {
    const logicalProfile = `/company/${encodeOpaqueUrlPathSegment(soleCompanyId)}`;
    const base = `/${locale}${localizeRoute(logicalProfile, locale)}`;
    const finOnly = new URLSearchParams();
    if (indexUiState.isFinland === false) finOnly.set('finland', '0');
    const q = finOnly.toString();
    return q ? `${base}?${q}` : base;
  }

  // Get localized job segment
  const jobSegments = { en: 'jobs', fi: 'tyopaikat', sv: 'lediga-jobb' };
  const jobSegment = jobSegments[locale] || 'jobs';
  // Get localized location segment
  const locationSegments = { en: 'location', fi: 'alue', sv: 'omrade' } as const;
  const locationSegment = (locationSegments as any)[locale] || 'location';
  const jobsCompanySegment = JOBS_COMPANY_PATH_SEGMENT[locale] || 'company';

  // Check if we have any search parameters (query, filters, sort, page > 0)
  const hasQuery = indexUiState.query && indexUiState.query.trim().length > 0;
  const hasFilters = indexUiState.refinementList && Object.keys(indexUiState.refinementList).length > 0;
  const hasSort = indexUiState.sort && indexUiState.sort !== 'relevance';
  const hasPage = indexUiState.page !== undefined && indexUiState.page !== null && indexUiState.page > 0;
  
  // Only add /jobs segment if we have search parameters AND the original path doesn't already have a job segment
  // If no search parameters and we have an original path, preserve it
  let path = '';
  if (hasQuery || hasFilters || hasSort || hasPage) {
    // Check if originalPath already has a job segment
    const hasJobSegment =
      originalPath &&
      (originalPath.includes('/jobs') ||
        originalPath.includes('/tyopaikat') ||
        originalPath.includes('/lediga-jobb') ||
        originalPath.includes('/jobb'));

    const preserveResourceLanding =
      originalPath && isSummerJobsResourcePathname(originalPath);
    
    if (hasJobSegment || preserveResourceLanding) {
      // Preserve the original path structure if it already has a job segment
      path = originalPath;
    } else {
      // Add the job segment if the original path doesn't have one
      path = `/${locale}/${jobSegment}`;
    }
  } else if (originalPath) {
    // English marketing home is canonical at `/`; preserve other locale roots and paths.
    path =
      locale === 'en' && (originalPath === '/en' || originalPath === '/en/')
        ? '/'
        : originalPath;
  } else {
    // Fallback to default behavior if no original path provided
    path = locale === 'en' ? '/' : `/${locale}`;
  }

  // Location in path: only encode the first selected location to keep URLs clean and canonical.
  // Multi-location combinations are not represented in path form.
  const locValues = indexUiState.refinementList?.location || [];
  
  // Check if the path already has a location segment
  const hasLocationInPath = originalPath && (
    originalPath.includes('/location/') ||
    originalPath.includes('/alue/') ||
    originalPath.includes('/omrade/') ||
    originalPath.includes('/område/') ||
    originalPath.includes('/omr%C3%A5de/')
  );
  
  if (locValues && locValues.length > 0) {
    // We have location values - add or update location segment using only the first value
    const firstLocValue = locValues[0];
    const encodedLocs = firstLocValue
      ? buildEncodedLocationSrpPathSegment(String(firstLocValue), locale)
      : '';
    
    if (encodedLocs) {
      if (!hasLocationInPath) {
        // Add location segment if it's not already in the path
        path += `/${locationSegment}/${encodedLocs}`;
      } else {
        // Replace existing location segment with updated locations
        const locationSegments = { en: 'location', fi: 'alue', sv: 'omrade' };
        const currentLocationSegment = (locationSegments as any)[locale] || 'location';
        
        // Remove existing location segment and add updated one
        // Handle both encoded and unencoded versions
        const encodedLocationSegment =
          currentLocationSegment === 'omrade' ? 'omrade' : currentLocationSegment;
        const legacyEncodedSegment =
          currentLocationSegment === 'omrade' ? 'omr%C3%A5de' : currentLocationSegment;
        const pathWithoutLocation = path.replace(new RegExp(`/(${currentLocationSegment}|${encodedLocationSegment})/[^/?]+`), '');
        const legacyCleaned = pathWithoutLocation.replace(
          new RegExp(`/(${legacyEncodedSegment})/[^/?]+`),
          ''
        );
        path = legacyCleaned + `/${currentLocationSegment}/${encodedLocs}`;
      }
    }
  } else if (hasLocationInPath) {
    // No location values but path has location segment - remove it
    const locationSegments = { en: 'location', fi: 'alue', sv: 'omrade' };
    const currentLocationSegment = (locationSegments as any)[locale] || 'location';
    
    // Remove existing location segment
    // Handle both encoded and unencoded versions
    const encodedLocationSegment =
      currentLocationSegment === 'omrade' ? 'omrade' : currentLocationSegment;
    const legacyEncodedSegment =
      currentLocationSegment === 'omrade' ? 'omr%C3%A5de' : currentLocationSegment;
    path = path
      .replace(new RegExp(`/(${currentLocationSegment}|${encodedLocationSegment})/[^/?]+`), '')
      .replace(new RegExp(`/(?:${legacyEncodedSegment})/[^/?]+`), '');
  }

  // Company in path (after optional location), same idea as location — never use ?companyId=
  const companyIdsForPath = indexUiState.refinementList?.companyId || [];
  const hasCompanyInJobsPath =
    originalPath &&
    /\/(?:company|yritys|foretag)\/[^/?]+/i.test(originalPath.split('?')[0] || '');

  if (companyIdsForPath.length === 1 && String(companyIdsForPath[0]).trim()) {
    const enc = encodeOpaqueUrlPathSegment(String(companyIdsForPath[0]).trim());
    if (!hasCompanyInJobsPath) {
      path += `/${jobsCompanySegment}/${enc}`;
    } else {
      const stripped = path.replace(/\/(?:company|yritys|foretag)\/[^/?]+/i, '');
      path = `${stripped}/${jobsCompanySegment}/${enc}`;
    }
  } else if (hasCompanyInJobsPath) {
    path = path.replace(/\/(?:company|yritys|foretag)\/[^/?]+/i, '');
  }

  // Category/role in path: when the current `field` refinement is exactly what the path
  // segment already encodes, keep the pretty path and omit redundant ?field= params —
  // otherwise pagination links on hub pages become noindexed query URLs. When the field
  // state diverges from the segment (user changed filters), strip the segment so the
  // query params are authoritative.
  let fieldEncodedInPath = false;
  const fieldValuesForPath = (indexUiState.refinementList?.field || []).filter(
    (v: unknown) => v != null && String(v).trim() !== '',
  );
  const pathOnlyForSegments = path.split('?')[0] || '';
  const categorySegmentMatch = pathOnlyForSegments.match(/\/(?:category|kategoria|kategori)\/([^/?]+)/i);
  const roleSegmentMatch = pathOnlyForSegments.match(/\/(?:role|ammatti|yrke)\/([^/?]+)/i);
  if (categorySegmentMatch) {
    const category = getCategoryBySlug(decodeURIComponent(categorySegmentMatch[1]));
    const expected = (category ? getCategoryFieldValuesForLocale(category, locale) : [])
      .map((v) => v.toLowerCase())
      .sort();
    const actual = fieldValuesForPath.map((v: string) => String(v).toLowerCase()).sort();
    if (category && expected.length === actual.length && expected.every((v, idx) => v === actual[idx])) {
      fieldEncodedInPath = true;
    } else {
      path = path.replace(/\/(?:category|kategoria|kategori)\/[^/?]+/i, '');
    }
  }
  if (roleSegmentMatch) {
    const roleDef = getRoleBySlug(decodeURIComponent(roleSegmentMatch[1]));
    if (roleDef && fieldValuesMatchRole(roleDef, locale, fieldValuesForPath.map(String))) {
      fieldEncodedInPath = true;
    } else {
      path = path.replace(/\/(?:role|ammatti|yrke)\/[^/?]+/i, '');
    }
  }

  // Add query if present
  if (indexUiState.query) params.set('search', indexUiState.query);

  // Add refinementList parameters
  if (indexUiState.refinementList) {
    for (const [attribute, values] of Object.entries(indexUiState.refinementList)) {
      if (attribute === 'location') continue; // already encoded in path
      if (attribute === 'companyId') continue; // encoded in path (or employer profile URL)
      if (attribute === 'field' && fieldEncodedInPath) continue; // encoded as /category/… or /role/… path segment
      if (attribute === 'sort' || attribute.includes('[sort]')) {
        // Skip sort from refinementList - it's handled separately
        continue;
      }
      const list = Array.isArray(values) ? values : [values as any];
      // Filter out empty/null/undefined values but preserve valid ones (including 0 and negative numbers for timestamps)
      const validValues = list.filter((v) => v !== undefined && v !== null && String(v).trim() !== '');
      if (validValues.length === 0) {
        continue;
      }
      validValues.forEach((value) => {
        const raw = String(value);
        // Lowercase most facet values for stable URLs; company IDs are opaque — preserve casing/symbols.
        const encoded =
          attribute.toLowerCase() === 'companyid' ? raw : raw.toLowerCase();
        params.append(attribute, encoded);
      });
    }
  }

  // Add sort parameter (omit if relevance to keep URL clean)
  if (indexUiState.sort && indexUiState.sort !== 'relevance') {
    params.set('sort', indexUiState.sort);
  }

  // Keep page explicit for non-first pages so pagination links are stable and predictable.
  // 0-based: page 0 = first page (omit), page 1 = second page, etc.
  if (indexUiState.page !== undefined && indexUiState.page !== null && indexUiState.page > 0) {
    params.set('page', indexUiState.page.toString());
  }

  // Finland filter: only put in URL when false so default (true) stays out of the query string
  if (indexUiState.isFinland === false) {
    params.set('finland', '0');
  }

  // View mode: only put in URL when 'map' so default (list) URLs stay clean
  if (indexUiState.view === 'map') {
    params.set('view', 'map');
  }

  const queryString = params.toString();
  const result = queryString ? `${path}?${queryString}` : path;
  
  return result;
}

/**
 * Maps a path to its corresponding route key
 * @param path The path to map (without language prefix)
 * @returns The corresponding route key or null if not found
 */
function mapPathToRouteKey(path: string): string | null {
  // Handle dynamic routes
  for (const lang of ['en', 'fi', 'sv'] as const) {
    if (path.startsWith(`/${LOCALIZED_PATHS[lang].job}/`)) {
      return 'route.jobDetailPage';
    }
  }

  // Try to find a matching route key in any language
  for (const lang of ['en', 'fi', 'sv'] as const) {
    const langMessages = messages[lang];
    const routeKey = Object.entries(langMessages).find(([key, value]) => key.startsWith('route.') && value === path)?.[0];

    if (routeKey) {
      return routeKey;
    }
  }

  return null;
}

/**
 * Checks if the current pathname represents a search results page (SRP)
 * @param pathname The URL pathname
 * @param searchParams The URL search parameters
 * @returns true if this is an SRP, false otherwise
 */
function isSearchResultsPage(pathname: string, searchParams: URLSearchParams): boolean {
  // Check for search parameters that indicate an active search
  const hasSearchParams = 
    searchParams.has('search') ||
    searchParams.has('query') ||
    searchParams.has('en_search[query]') ||
    searchParams.has('fi_hae[query]') ||
    searchParams.has('sv_sok[query]') ||
    searchParams.has('sort') ||
    searchParams.has('page') ||
    searchParams.has('jobType') ||
    searchParams.has('isSummerJob') ||
    searchParams.has('remoteness') ||
    searchParams.has('location') ||
    searchParams.has('salaryRangeMin') ||
    searchParams.has('vacationWeeks') ||
    searchParams.has('companyHeadcountRangeMax') ||
    Array.from(searchParams.keys()).some(key => key.includes('[refinementList]'));

  // Check if pathname is home page or search page
  const segments = pathname.split('/').filter(Boolean);
  const firstSegment = segments[0]?.toLowerCase();
  const isLocale = firstSegment && ['en', 'fi', 'sv'].includes(firstSegment);
  
  // Home page (root or locale-only)
  if (segments.length === 0 || (segments.length === 1 && isLocale)) {
    return true;
  }

  // Check for jobs listing paths (plural, not singular job detail pages)
  // Job detail uses singular: job/tyopaikka/jobb
  // Jobs listing uses plural: jobs/tyopaikat/lediga-jobb
  const jobsListingSegments = ['jobs', 'tyopaikat', 'lediga-jobb'];
  const jobsIndex = isLocale 
    ? segments.findIndex((s, idx) => idx > 0 && jobsListingSegments.includes(s))
    : segments.findIndex(s => jobsListingSegments.includes(s));
  
  if (jobsIndex !== -1) {
    // This is a jobs listing page (SRP)
    return true;
  }
  
  // Check if this is a job detail page (singular job path)
  // Job detail pattern: /{locale}/{job}/{jobId}/{companyId}/{seoSlug}
  const jobDetailPattern = /^\/(en|fi|sv)\/(job|tyopaikka|jobb)\/([^/]+)\/([^/]+)/;
  if (jobDetailPattern.test(pathname)) {
    return false; // Job detail page, not SRP
  }

  return hasSearchParams;
}

/**
 * Converts a URL pathname to the same route in a target language.
 * Accepts either a pathname (e.g. /sv/jobs) or a full URL (e.g. http://localhost:3000/sv/jobs); full URLs are normalized to pathname + search.
 *
 * @param pathnameOrUrl The current URL pathname, or full URL (pathname may include search parameters)
 * @param targetLanguage The target language code (e.g., 'en', 'fi', 'sv')
 * @param intl The IntlShape object from react-intl
 * @returns The same page in the target language (path only), or home page for SRPs
 */
export function convertUrlToLanguage(pathnameOrUrl: string, targetLanguage: string, intl: IntlShape): string {
  let pathname = pathnameOrUrl;
  if (pathnameOrUrl.includes('://')) {
    try {
      const url = new URL(pathnameOrUrl);
      pathname = url.pathname + (url.search ? url.search : '');
    } catch {
      pathname = pathnameOrUrl;
    }
  }

  // Split pathname and search parameters
  const [cleanPathname, searchParamsString] = pathname.split('?');
  const searchParams = new URLSearchParams(searchParamsString || '');
  
  // Check if this is a search results page (SRP)
  // For SRPs, we can't preserve facet values across languages, so go to home page
  if (isSearchResultsPage(cleanPathname, searchParams)) {
    return `/${targetLanguage}`;
  }
  
  // Extract current locale and path without locale prefix
  const segments = cleanPathname.split("/").filter(Boolean);
  let currentLocale: 'en' | 'fi' | 'sv' = 'en';
  let pathWithoutLocale = cleanPathname;
  
  // Check if first segment is a locale
  const firstSegment = segments[0]?.toLowerCase();
  if (firstSegment && ['en', 'fi', 'sv'].includes(firstSegment)) {
    currentLocale = firstSegment as 'en' | 'fi' | 'sv';
    pathWithoutLocale = '/' + segments.slice(1).join('/');
    if (pathWithoutLocale === '/') {
      // Handle language home routes (e.g., "/en" -> "/fi")
      return `/${targetLanguage}`;
    }
  }
  
  // Handle job detail pages specially
  // Pattern: /{job}/{jobId}/{companyId}/{seoSlug}
  const jobSlugs = Object.values(LOCALIZED_PATHS).map((l) => l.job);
  const jobDetailPattern = new RegExp(`^/(${jobSlugs.map(s => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})/([^/]+)/([^/]+)(?:/(.+))?$`);
  const jobMatch = pathWithoutLocale.match(jobDetailPattern);
  
  if (jobMatch) {
    // Extract jobId, companyId, and seoSlug
    const [, , jobId, companyId, seoSlug] = jobMatch;
    const targetJobSlug = LOCALIZED_PATHS[targetLanguage as keyof typeof LOCALIZED_PATHS].job;
    
    // Preserve jobId and companyId, keep seoSlug as-is (it will be regenerated by the page if needed)
    const newPath = seoSlug 
      ? `/${targetLanguage}/${targetJobSlug}/${jobId}/${companyId}/${seoSlug}`
      : `/${targetLanguage}/${targetJobSlug}/${jobId}/${companyId}`;
    
    return searchParamsString ? `${newPath}?${searchParamsString}` : newPath;
  }
  
  // For static pages, use the route localization system
  // Convert localized path back to English equivalent
  const englishPath = delocalizeRoute(pathWithoutLocale, currentLocale);
  
  // Convert English path to target language
  const localizedPath = localizeRoute(englishPath, targetLanguage as 'en' | 'fi' | 'sv');
  
  // Add target language prefix (including for English)
  const newPath = `/${targetLanguage}${localizedPath}`;
  
  // Preserve any search parameters for static pages
  return searchParamsString ? `${newPath}?${searchParamsString}` : newPath;
}